import {
  from, map, of, switchMap, take, withLatestFrom
} from 'rxjs';
import { hashMessageToSha256, injectNielsenScript } from '../../../externals/utils';
import { Disposable } from '../../core';
import Media from '../../core/media/Media';
import { MONITORING_CONSENTED } from '../../core/monitoring/types';
import { removeScriptByUrlContains } from '../../utils/scriptManager';
import { EVENT_END, EVENT_LOADEDMETADATA, EVENT_PLAYHEAD_POSITION, EVENT_STOP } from '../playback/const';
import {
  NIELSEN_DEBUG, NIELSEN_EVENT_END, NIELSEN_EVENT_LOADEDMETADATA,
  NIELSEN_EVENT_SET_PLAYHEAD_POSITION, NIELSEN_EVENT_STOP, NIELSEN_INSTANCE_NAME, NIELSEN_LIB_NAME
} from './const';

const LIVE = 'LIVE';
const TIMESHIFTING = 'TIMESHIFTING';

const EVENT_MAPPING = {
  [EVENT_END]: NIELSEN_EVENT_END,
  [EVENT_LOADEDMETADATA]: NIELSEN_EVENT_LOADEDMETADATA,
  [EVENT_PLAYHEAD_POSITION]: NIELSEN_EVENT_SET_PLAYHEAD_POSITION,
  [EVENT_STOP]: NIELSEN_EVENT_STOP
};

export const NIELSEN_URL_DOMAIN = 'cdn-gl.nmrodam.com';

export default class Nielsen extends Disposable {
  constructor(player) {
    super();
    injectNielsenScript(NIELSEN_LIB_NAME, NIELSEN_URL_DOMAIN);

    this.instanceName = `${NIELSEN_INSTANCE_NAME}_${Math.floor(Math.random() * 10000000000)}`;
    this.nlsQ = window[NIELSEN_LIB_NAME].nlsQ;

    Nielsen.createInitStream(player).subscribe(this.createSdkInstance.bind(this));

    Nielsen.mapPlayerTrackingToNielsenEvents(player.playbackTracking.trackings$)
      .subscribe(this.triggerEvents.bind(this));
  }

  static mapPlayerTrackingToNielsenEvents = (trackings$) => trackings$
    .pipe(
      map((e) => (e.eventName === EVENT_LOADEDMETADATA
        ? { eventName: e.eventName, data: Nielsen.mapLoadMetadata(e.data) }
        : e
      ))
    );

  static createInitStream({ medias$, playerConfig$ }) {
    return medias$.pipe(
      withLatestFrom(playerConfig$),
      switchMap(([medias, playerConfig]) => (playerConfig.publicId
        ? from(hashMessageToSha256(playerConfig.publicId))
        : of(''))
        .pipe(
          map((hemSha1) => ({
            apid: medias.markers.nielsen.apid,
            hem_sha1: playerConfig.consent.nielsen === MONITORING_CONSENTED ? hemSha1 : '',
            optout: Boolean(playerConfig.consent.nielsen !== MONITORING_CONSENTED).toString(),
            enableFpid: Boolean(playerConfig.consent.nielsen === MONITORING_CONSENTED).toString(),
            nol_sdkDebug: playerConfig.debug && playerConfig.debugDetails?.nielsen !== false ? NIELSEN_DEBUG : null
          }))
        )),
      take(1)
    );
  }

  static mapLoadMetadata({ media, shifting, config }) {
    const { apid, ...nielsenNoApid } = media.markers.nielsen;
    return ({
      ...nielsenNoApid,
      cli_md: Nielsen.getCliMd(media, shifting),
      nol_p12: Nielsen.getNolP12(media, config),
      nol_p13: Nielsen.getNolP13(media, shifting)
    }
    );
  }

  createSdkInstance({ apid, ...contentMetadataObject }) {
    this.nSdkInstance = this.nlsQ(apid, this.instanceName, contentMetadataObject);
  }

  triggerEvents({ eventName, data }) {
    this.nSdkInstance.ggPM(EVENT_MAPPING[eventName], data);
  }

  static getCliMd(media, shifting) {
    return Media.isLive(media)
      ? (shifting && TIMESHIFTING) || LIVE
      : media.markers.nielsen.cli_md;
  }

  static getNolP12(media, { diffusion: { mode, position, length } = {} }) {
    return mode !== 'tunnel' ? media.markers.nielsen?.nol_p12 : `p12,tunnel_${position}_${length}`;
  }

  static getNolP13(media, shifting) {
    return (Media.isLive(media) && shifting && 'p13,n') || media.markers.nielsen.nol_p13;
  }

  dispose() {
    const lib = window[NIELSEN_LIB_NAME];
    const n = lib?.[this.instanceName];

    if (n) {
      removeScriptByUrlContains(NIELSEN_URL_DOMAIN);

      const noop = () => { };
      n.q = [];
      n.te = [];
      n.ggPM = noop;
      n.trackEvent = noop;

      delete lib[this.instanceName];
    }

    super.dispose();
  }
}
